import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { importITunes, importFolder } from '../api';
import NavControls from '../components/NavControls';

export default function Importer() {
  const [status, setStatus] = useState(null);
  const [results, setResults] = useState(null);
  const [loveBest, setLoveBest] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [copyFiles, setCopyFiles] = useState(false);
  const [sourcePath, setSourcePath] = useState('/itunes');
  const [xmlPath, setXmlPath] = useState('');

  // Folder import state
  const [folderStatus, setFolderStatus] = useState(null);
  const [folderResults, setFolderResults] = useState(null);
  const [folderPath, setFolderPath] = useState('/imports');

  const runImport = useCallback(async (file) => {
    setStatus('uploading');
    setResults(null);
    try {
      const res = await importITunes(file, loveBest, newOnly, copyFiles, sourcePath, xmlPath);
      if (res.data.error) {
        setStatus('error');
        setResults(res.data);
      } else {
        setResults(res.data);
        setStatus('done');
      }
    } catch (err) {
      setStatus('error');
      setResults({ error: err.response?.data?.error || err.message });
    }
  }, [loveBest, newOnly, copyFiles, sourcePath, xmlPath]);

  const runFolderImport = useCallback(async () => {
    setFolderStatus('importing');
    setFolderResults(null);
    try {
      const res = await importFolder(folderPath);
      if (res.data.error) {
        setFolderStatus('error');
        setFolderResults(res.data);
      } else {
        setFolderResults(res.data);
        setFolderStatus('done');
      }
    } catch (err) {
      setFolderStatus('error');
      setFolderResults({ error: err.response?.data?.error || err.message });
    }
  }, [folderPath]);

  const onDrop = useCallback(async (files) => {
    const file = files[0];
    if (!file) return;
    await runImport(file);
  }, [runImport]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'text/xml': ['.xml'] }, multiple: false
  });

  return (
    <div>
      <div className="page-header">
        <h1>Import Music</h1>
        <p>Import new downloads from a folder, or migrate your full library from iTunes.</p>
      </div>

      <div style={{ maxWidth: 600 }}>

        {/* ── Folder Import ─────────────────────────────────────── */}
        <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 4 }}>📁 Import from Folder</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
            Drop new downloads into the folder, then click Import. Files are moved (not copied)
            into your managed music library and tagged automatically from embedded metadata.
            Empty folders are cleaned up after import.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              className="search-input"
              value={folderPath}
              onChange={e => setFolderPath(e.target.value)}
              placeholder="/imports"
              style={{ flex: 1 }}
            />
            <button
              className="btn"
              onClick={runFolderImport}
              disabled={!folderPath || folderStatus === 'importing'}
            >
              {folderStatus === 'importing' ? '⏳ Importing…' : 'Import'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
            Default: <code>/imports</code> — mount your downloads folder here in Docker.
            Supports .mp3, .m4a, .aac, .flac, .alac, .ogg, .wav, .aiff
          </div>

          {folderStatus === 'done' && folderResults && (
            <div style={{ marginTop: 16, padding: 16, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ color: '#4ade80', fontWeight: 600, marginBottom: 8 }}>✅ Import Complete</div>
              <p>Imported: <strong>{folderResults.imported}</strong> tracks</p>
              <p>Skipped (already in library): <strong>{folderResults.skipped}</strong></p>
              {folderResults.files_moved > 0 && <p>Files moved: <strong style={{ color: 'var(--accent)' }}>{folderResults.files_moved}</strong></p>}
              {folderResults.art_extracted > 0 && <p>Album art extracted: <strong>{folderResults.art_extracted}</strong></p>}
              {folderResults.dirs_cleaned > 0 && <p>Empty dirs removed: <strong>{folderResults.dirs_cleaned}</strong></p>}
              {folderResults.errors?.length > 0 && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13 }}>
                    {folderResults.errors.length} errors
                  </summary>
                  <pre style={{ marginTop: 6, fontSize: 11, color: 'var(--text-muted)', overflow: 'auto', maxHeight: 180 }}>
                    {JSON.stringify(folderResults.errors.slice(0, 20), null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}

          {folderStatus === 'error' && (
            <div style={{ marginTop: 16, padding: 16, background: 'var(--bg3)', borderRadius: 8 }}>
              <div style={{ color: '#f87171', fontWeight: 600, marginBottom: 6 }}>❌ Import Failed</div>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{folderResults?.error}</p>
            </div>
          )}
        </div>

        {/* ── iTunes XML Import ──────────────────────────────────── */}
        <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 24, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>How to export from iTunes / Music</h3>
          <ol style={{ color: 'var(--text-muted)', lineHeight: 1.8, paddingLeft: 20 }}>
            <li>Open the Music app on your Mac</li>
            <li>Go to <strong style={{ color: 'var(--text)' }}>File → Library → Export Library…</strong></li>
            <li>Save the file as <strong style={{ color: 'var(--text)' }}>iTunes Music Library.xml</strong></li>
            <li>Drop it below</li>
          </ol>
        </div>

        {/* Options */}
        <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
          <h3 style={{ marginBottom: 12 }}>Import Options</h3>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14 }}>
            <input
              type="checkbox"
              checked={loveBest}
              onChange={e => setLoveBest(e.target.checked)}
            />
            <div>
              <div style={{ color: 'var(--text)' }}>Love tracks from "Best" playlists</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                Marks all tracks in playlists containing "Best" in the name as loved. Recommended for fresh imports only.
              </div>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, marginTop: 12 }}>
            <input
              type="checkbox"
              checked={newOnly}
              onChange={e => setNewOnly(e.target.checked)}
            />
            <div>
              <div style={{ color: 'var(--text)' }}>Import new tracks only</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                Skip tracks already in your library. Preserves your existing metadata, play counts, and loves.
              </div>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 14, marginTop: 12 }}>
            <input
              type="checkbox"
              checked={copyFiles}
              onChange={e => setCopyFiles(e.target.checked)}
            />
            <div>
              <div style={{ color: 'var(--text)' }}>Copy files to MusicMix folder</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
                Copies audio files to your managed music directory and extracts album art
              </div>
            </div>
          </label>

          {copyFiles && (
            <div style={{ marginTop: 12, marginLeft: 28 }}>
              <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
                Source music folder path
              </label>
              <input
                className="search-input"
                value={sourcePath}
                onChange={e => setSourcePath(e.target.value)}
                placeholder="/itunes"
                style={{ maxWidth: 400 }}
              />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Default: /itunes (iTunes mounted via Docker). Change if your files are elsewhere.
              </div>
            </div>
          )}          
        </div>

        <div style={{ background: 'var(--bg2)', borderRadius: 12, padding: 20, marginBottom: 16 }}>
          <h3 style={{ marginBottom: 8 }}>Library XML path (server-side)</h3>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            If your Library.xml is accessible on the server (e.g. via Docker volume mount), enter the path here instead of uploading.
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              className="search-input"
              value={xmlPath}
              onChange={e => setXmlPath(e.target.value)}
              placeholder="/music/Library.xml"
              style={{ flex: 1 }}
            />
            <button
              className="btn"
              onClick={() => runImport(null)}
              disabled={!xmlPath || status === 'uploading'}
            >
              Import
            </button>
          </div>
        </div>

        <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          <div style={{ fontSize: 40 }}>📂</div>
          {isDragActive
            ? <p style={{ color: 'var(--accent)', marginTop: 8 }}>Drop it here!</p>
            : <>
                <p style={{ fontSize: 16, marginTop: 8 }}>Drop your iTunes Library.xml here</p>
                <p>or click to browse</p>
              </>
          }
        </div>

        {status === 'uploading' && (
          <div style={{ marginTop: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
            ⏳ Importing your library… this may take a minute for large libraries.
          </div>
        )}

        {status === 'done' && results && (
          <div style={{ marginTop: 24, background: 'var(--bg2)', borderRadius: 12, padding: 20 }}>
            <h3 style={{ color: '#4ade80', marginBottom: 12 }}>✅ Import Complete</h3>
            <p>Imported: <strong>{results.imported}</strong> tracks</p>
            <p>Skipped: <strong>{results.skipped}</strong> tracks</p>
            {results.loved_from_best !== undefined && (
              <p>Loved from Best playlists: <strong style={{ color: 'var(--accent)' }}>{results.loved_from_best}</strong> tracks</p>
            )}
            {results.playlists_imported !== undefined && (
              <p>Playlists imported: <strong>{results.playlists_imported}</strong></p>
            )}
            {results.errors?.length > 0 && (
              <details style={{ marginTop: 12 }}>
                <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {results.errors.length} errors
                </summary>
                <pre style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)', overflow: 'auto', maxHeight: 200 }}>
                  {JSON.stringify(results.errors.slice(0, 20), null, 2)}
                </pre>
              </details>
            )}
            {results.files_copied !== undefined && (
              <p>Files copied: <strong style={{ color: 'var(--accent)' }}>{results.files_copied}</strong></p>
            )}
            {results.files_missing > 0 && (
              <p style={{ color: '#f87171' }}>⚠️ Missing source files: <strong>{results.files_missing}</strong></p>
            )}
            {results.art_extracted !== undefined && (
              <p>Album art extracted: <strong>{results.art_extracted}</strong></p>
            )}            
          </div>
        )}

        {status === 'error' && (
          <div style={{ marginTop: 24, background: 'var(--bg2)', borderRadius: 12, padding: 20 }}>
            <h3 style={{ color: '#f87171', marginBottom: 8 }}>❌ Import Failed</h3>
            <p style={{ color: 'var(--text-muted)' }}>{results?.error}</p>
          </div>
        )}
      </div>
    </div>
  );
}